import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../redux";
import { bugTicketActions } from "../redux/reducers/bugTicketSlice";
import { Button, Dialog, InputField, View, Text } from "./base";
import { Icon } from "./base/Icon";

type SubmitBugDialogProps = {
  isOpen?: boolean;
  onClose?: () => void;
};

export const SubmitBugDialog = ({ isOpen = false, onClose = () => {} }: SubmitBugDialogProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isLoading = useAppSelector((state) => state.bugTicket?.isLoadingCreate || false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setSubmitted(false);
    setTicketId(null);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    if (!description.trim()) {
      setError("Description is required");
      return;
    }

    setError(null);

    const result = await dispatch(
      bugTicketActions.submitBugTicket({
        title: title.trim(),
        description: description.trim(),
      }),
    );

    if (bugTicketActions.submitBugTicket.fulfilled.match(result)) {
      setTicketId(result.payload.id);
      setSubmitted(true);
    } else {
      setError((result.payload as string) || "Failed to submit bug report. Please try again.");
    }
  };

  // Success state
  if (submitted && ticketId) {
    return (
      <Dialog
        isOpen={isOpen}
        onClose={handleClose}
        title="Bug Report Submitted"
        className="w-full max-w-md"
      >
        <View className="flex flex-col items-center p-6 space-y-4">
          <View className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
            <Icon
              name="check"
              size={32}
              className="text-green-600 dark:text-green-400"
            />
          </View>

          <View className="flex flex-col items-center space-y-2">
            <Text className="text-lg font-semibold text-gray-900 dark:text-white">Thank you!</Text>
            <Text className="text-gray-600 dark:text-gray-400">
              Your bug report has been submitted successfully. We'll notify you when it gets a reply or its status changes.
            </Text>
            <View className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3 mt-4 w-full text-center">
              <Text className="text-sm text-gray-500 dark:text-gray-400">Ticket ID:</Text>
              <Text className="text-base font-mono font-medium text-gray-900 dark:text-white">{ticketId}</Text>
            </View>
          </View>

          <Button
            variant="brand"
            onClick={() => {
              const id = ticketId;
              handleClose();
              navigate(`/bug-reports/${id}`);
            }}
            className="w-full mt-2"
          >
            View my bug report
          </Button>
          <Button
            variant="neutral"
            onClick={handleClose}
            className="w-full"
          >
            Close
          </Button>
        </View>
      </Dialog>
    );
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Submit Bug Report"
      className="w-full max-w-lg"
    >
      <View className="flex flex-col overflow-y-auto max-h-[80vh] p-4 space-y-4">
        {error && (
          <View className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <Text className="text-sm text-red-600 dark:text-red-400">{error}</Text>
          </View>
        )}

        <InputField
          label="Title *"
          value={title}
          onChange={setTitle}
          placeholder="Brief description of the issue"
          isDisabled={isLoading}
        />

        <InputField
          label="Description *"
          type="textarea"
          value={description}
          onChange={setDescription}
          placeholder="Please describe the bug in detail. What did you expect to happen? What actually happened? Steps to reproduce help a lot."
          isDisabled={isLoading}
          className="min-h-[240px]"
        />

        <View className="flex flex-row items-center justify-end gap-2 pt-2">
          <Button
            variant="neutral"
            onClick={handleClose}
            isDisabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="brand"
            onClick={handleSubmit}
            isDisabled={isLoading || !title.trim() || !description.trim()}
          >
            {isLoading ? (
              <>
                <Icon
                  name="refresh"
                  size={16}
                  className="animate-spin mr-2"
                />
                Submitting...
              </>
            ) : (
              <>
                <Icon
                  name="send"
                  size={16}
                  className="mr-2"
                />
                Submit Bug Report
              </>
            )}
          </Button>
        </View>
      </View>
    </Dialog>
  );
};
